import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ShippingLocation, Variation } from '@/types';
import { useStoreInfo } from '@/lib/store';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDomain() {
  return window.location.hostname;
}

export function getStoreId() {
  const domain = getDomain();
  const storeId = domain.split('.')[0];
  return storeId;
}

// Cache currency info to avoid repeated store access
let cachedCurrency: string | null = null;
let cachedCurrencySymbol: string | null = null;

export function formatPrice(price: number | string) {
  try {
    // Get currency from cache or store
    if (!cachedCurrency) {
      const { storeInfo } = useStoreInfo.getState();
      cachedCurrency = storeInfo?.currency || storeInfo?.currenCy || 'NGN';
      cachedCurrencySymbol = storeInfo?.currency_symbol || cachedCurrency;
    }
    
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    // Handle invalid numbers
    if (isNaN(numericPrice)) {
      return '₦0.00';
    }
    
    // For NGN and other currencies that might not be fully supported by Intl
    // Use the currency symbol directly
    if (cachedCurrency === 'NGN' || cachedCurrencySymbol === 'NGN') {
      return `₦${numericPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    // Try using Intl.NumberFormat
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cachedCurrency.toUpperCase(),
    }).format(numericPrice);
  } catch (error) {
    // Fallback: simple formatting
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return '₦0.00';
    return `₦${numericPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export function formatPriceRange(variations: Variation[]) {
  if (!variations || variations.length === 0) return '';

  const prices = variations.map(v => parseInt(v.variation_amount));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  if (minPrice === maxPrice) {
    return formatPrice(minPrice);
  }

  return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
}

export function getLowestPrice(variations: Variation[]) {
  if (!variations || variations.length === 0) return 0;
  return Math.min(...variations.map(v => parseInt(v.variation_amount)));
}

export function truncateText(text: string, length: number) {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

export function fetchWithTimeout(resource: string, options: RequestInit & { timeout?: number } = {}) {
  const { timeout = 10000 } = options;
  
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  try {
    const response = fetch(resource, {
      ...options,
      headers: {
        'Accept': 'application/json',
        ...(isIOS ? {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'If-None-Match': new Date().getTime().toString()
        } : {}),
        ...(options.headers || {})
      },
      ...(isIOS ? {
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit'
      } : {
        signal: controller.signal
      })
    });
    clearTimeout(id);
    return response;
  } catch (error: unknown) {
    clearTimeout(id);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    throw error;
  }
}

export function setCache(key: string, data: any, days = 10) {
  const item = {
    value: data,
    timestamp: new Date().getTime() + (days * 24 * 60 * 60 * 1000)
  };
  try {
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error: unknown) {
    console.warn('Failed to set cache:', error);
  }
}

export function getCache(key: string) {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const { value, timestamp } = JSON.parse(item);
    if (new Date().getTime() > timestamp) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  } catch (error) {
    console.warn('Failed to get cache:', error);
    return null;
  }
}

export function setShippingPreferences(country: string, location: string) {
  setCache('shipping_country', country, 30);
  setCache('shipping_location', location, 30);
}

export function getShippingPreferences(): { country: string | null; location: string | null } {
  return {
    country: getCache('shipping_country'),
    location: getCache('shipping_location')
  };
}

export function filterLocations(locations: ShippingLocation[], search: string): ShippingLocation[] {
  const searchLower = search.toLowerCase();
  return locations.filter(
    location => 
      location.name?.toLowerCase().includes(searchLower) ||
      location.country.toLowerCase().includes(searchLower)
  );
}

export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePhone(phone: string): boolean {
  return phone.length >= 10 && /^\+?[\d\s-]+$/.test(phone);
}

export function getCountries(locations: ShippingLocation[]): string[] {
  return [...new Set(locations.map(location => location.country))];
}

export function getLocationsByCountry(locations: ShippingLocation[], country: string): ShippingLocation[] {
  return locations.filter(location => location.country === country);
}

export async function retryWithBackoff(fn: () => Promise<any>, maxRetries = 3, baseDelay = 1000): Promise<any> {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i === maxRetries - 1) break;
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, i)));
    }
  }
  throw lastError;
}